import { marked, Renderer } from 'marked';
import DOMPurify from 'isomorphic-dompurify';
import TurndownService from 'turndown';
import { TrustedHTML } from 'trusted-types/lib';

import { SharedTextUtility } from './text.utility';
import { SharedCommonUtility } from './common.utility';
import { ICodeHighlighter, ICodeHighlighterResponse } from '../interfaces/code-highlighter.interface';

enum TokenType {
  HEADING = 'heading',
  HR = 'hr',
  LINK = 'link',
  LIST = 'list',
  LIST_ITEM = 'listItem',
  TABLE = 'table',
  TEXT = 'text',
}

export enum RenderType {
  DEFAULT = 'DEFAULT',
  NOTIFICATION = 'NOTIFICATION',
  AICHAT = 'AICHAT',
}

const RenderTypeSanitizeConfig: Record<RenderType, any> = {
  DEFAULT: {},
  NOTIFICATION: {},
  AICHAT: {
    ADD_ATTR: ['target', 'class'],
    ADD_TAGS: ['pre', 'code'],
  },
};

type ITokenClasses = Partial<Record<TokenType, string>>;

const RenderTypeLinkClasses: Record<RenderType, ITokenClasses> = {
  DEFAULT: {},
  NOTIFICATION: {},
  AICHAT: {
    [TokenType.LINK]: 'ds-link inline',
    [TokenType.LIST_ITEM]: 'ds-link inline',
  },
};

interface Token {
  type: TokenType;
  raw: string;
  text?: string;
}

interface MarkedOptions {
  renderer: Renderer;
  walkTokens?: ((token: Token) => void) | undefined;
  breaks?: boolean | undefined;
  gfm?: boolean | undefined;
}

export class MarkdownUtility {
  private static readonly markedOptions: Map<RenderType, MarkedOptions> = new Map();
  private static readonly tokenTypesToChangeForNotification: Set<string> = new Set([
    TokenType.HEADING,
    TokenType.HR,
    TokenType.LINK,
    TokenType.LIST,
    TokenType.TABLE,
  ]);

  private static readonly turndownService: TurndownService = new TurndownService({
    headingStyle: 'atx',
  });

  private static codeHighlighter: ICodeHighlighter | null = null;

  /**
   * Special symbols combination that is replaced with <br> tag by the marked library
   */
  public static readonly br: string = '  \n';

  private static readonly BLANK_LINE_PLACEHOLDER: string = '\u200B\u200C\u200D'; // Zero-width characters

  /**
   * Sets the code highlighter to be used, allowing for client-side code to provide syntax highlighting functionality.
   */
  public static setCodeHighlighter(highlighter: ICodeHighlighter | null): void {
    this.codeHighlighter = highlighter;
  }

  private static getMarkedOptions(type: RenderType): any {
    if (!this.markedOptions.has(type)) {
      this.markedOptions.set(type, this.buildMarkedOptions(type));
    }

    return this.markedOptions.get(type);
  }

  private static buildMarkedOptions(type: RenderType): any {
    switch (type) {
      case RenderType.NOTIFICATION:
        return this.buildMarkedOptionsForNotification();
      case RenderType.AICHAT:
        return this.buildMarkedOptionsForAIChat();
      default:
        return this.buildDefaultMarkedOptions();
    }
  }

  private static buildMarkedOptionsForNotification(): any {
    const renderer: Renderer = new Renderer();

    renderer.code = function (text: string): string {
      return `<code>${text}</code>`;
    };

    renderer.codespan = function (text: string): string {
      return `<code>${SharedTextUtility.unescapeAmpersands(text)}</code>`;
    };

    renderer.paragraph = renderer.text;

    return {
      renderer: renderer,
      walkTokens: this.changeTokenTypeToText,
    };
  }

  private static changeTokenTypeToText = (token: Token): void => {
    if (MarkdownUtility.tokenTypesToChangeForNotification.has(token.type)) {
      token.type = TokenType.TEXT;

      if (SharedCommonUtility.isNullish(token['text'])) {
        token['text'] = token.raw;
      }
    }
  };

  private static buildMarkedOptionsForAIChat(): MarkedOptions {
    const renderer: Renderer = new Renderer();
    const tokenClasses: ITokenClasses = RenderTypeLinkClasses[RenderType.AICHAT];

    renderer.code = function (text: string, language: string): string {
      const restoredText: string = text.replace(new RegExp(MarkdownUtility.BLANK_LINE_PLACEHOLDER, 'g'), '');

      if (MarkdownUtility.codeHighlighter && language) {
        const unescapedText: string = SharedTextUtility.unescapeHtmlTags(restoredText);
        const codeHighlighterResponse: ICodeHighlighterResponse = MarkdownUtility.codeHighlighter.highlight(
          unescapedText,
          language,
        );
        return `<pre><code class="hljs language-${codeHighlighterResponse.language}">${codeHighlighterResponse.code}</code></pre>`;
      }

      const escapedText: string = SharedTextUtility.escapeHtmlTags(restoredText);
      return `<pre><code>${escapedText}</code></pre>`;
    };

    renderer.codespan = function (text: string): string {
      const escapedText: string = SharedTextUtility.unescapeAmpersands(text.trim());
      return `<code>${escapedText}</code>`;
    };

    renderer.hr = function (): string {
      return `<hr role="presentation" aria-label="citations">`;
    };

    renderer.listitem = function (text: string): string {
      const patterns: { pages: RegExp; lines: RegExp } = {
        pages: /(?<![\w\-\/])p([0-9]+(?:-[0-9]+)?)(?![\w\-\/])/g, // matches p1, p123, p123-456 not within URLs/paths
        lines: /(?<![\w\-\/p])([0-9]+(-[0-9]+))(?![\w\-\/])(?![^<]*[">])/g, // matches 123-456 not within URLs/paths/HTML
      };

      let processedText: string = text;

      for (const [type, pattern] of Object.entries(patterns)) {
        processedText = processedText.replace(pattern, (fullMatch: string, rangeMatch: string) => {
          const [start, end] = rangeMatch.split('-');

          // Determine text based on type and whether it's a range
          const descriptor: string = type === 'pages' && !end ? 'page' : `${type}`;
          const ariaLabel: string = end ? `${descriptor} ${start} to ${end}` : `${descriptor} ${start}`;

          return `<span aria-label="${ariaLabel}">${fullMatch}</span>`;
        });
      }

      const listItemClass: string = tokenClasses[TokenType.LIST_ITEM];
      if (listItemClass) {
        const linkRegex: RegExp = /<a([^>]*)>/g;
        if (linkRegex.test(text)) {
          const styledText: string = text.replace(/<a([^>]*)>/g, `<a$1 class="${listItemClass}">`);
          return `<li>${styledText}</li>`;
        }
      }

      return `<li>${processedText}</li>`;
    };

    renderer.link = function (href: string, title: string | null, text: string): string {
      const classAttr: string = tokenClasses[TokenType.LINK] ? ` class="${tokenClasses[TokenType.LINK]}"` : '';
      const ariaLabelAttr: string = title ? ` aria-label="${title}"` : '';
      return `<a href="${href}" target="_blank"${classAttr}${ariaLabelAttr}>${text}</a>`;
    };

    return {
      gfm: true,
      breaks: true,
      renderer: renderer,
    };
  }

  private static buildDefaultMarkedOptions(): MarkedOptions {
    const renderer: Renderer = new Renderer();

    renderer.code = function (text: string): string {
      return `<pre><code>${text}</code></pre>`;
    };

    renderer.codespan = function (text: string): string {
      return `<code>${SharedTextUtility.unescapeAmpersands(text)}</code>`;
    };

    return {
      breaks: true,
      renderer: renderer,
    };
  }

  /**
   * Preprocesses markdown content to protect blank lines in fenced code blocks
   * from being misinterpreted by the marked parser.
   */
  private static preprocessCodeBlocks(value: string): string {
    return value.replace(
      /(```[a-z]*\n)([\s\S]*?)(\n```)/g,
      (match: string, startFence: string, content: string, endFence: string) => {
        const protectedContent: string = content.replace(/\n\s*\n/g, `\n${this.BLANK_LINE_PLACEHOLDER}\n`);
        return startFence + protectedContent + endFence;
      },
    );
  }

  public static render(value: string, type: RenderType = RenderType.DEFAULT): string {
    let processedValue: string = value;

    if (type === RenderType.AICHAT) {
      processedValue = this.preprocessCodeBlocks(value);
    }

    const escapedValue: string = SharedTextUtility.escapeHtmlTags(processedValue);
    const compiledHtml: string = marked(escapedValue, this.getMarkedOptions(type));
    const trustedHtml: TrustedHTML = DOMPurify.sanitize(compiledHtml, RenderTypeSanitizeConfig[type]);
    return trustedHtml.toString();
  }

  public static sanitize(value: string, options: Record<string, any> = {}): string {
    const workingValue: string = SharedTextUtility.escapeHtmlTags(value);
    return DOMPurify.sanitize(workingValue, {
      USE_PROFILES: { html: false },
      ...options,
    });
  }

  /**
   * Converts html content to markdown.
   *
   * @param html html content to convert to markdown.
   * @param options options for the conversion.
   * @param {boolean} [options.keepCodeTagsContent=true] Whether to preserve the content inside code tags. If false, the content inside code tags will also be converted to its html representation.
   *
   * @returns The markdown representation of the provided html.
   */
  public static htmlToMarkdown(html: string, options: { keepCodeTagsContent: boolean } = { keepCodeTagsContent: true }): string {
    if (options.keepCodeTagsContent) {
      const regex: RegExp = new RegExp(/<code>(.*?)<\/code>/g);
      const escapedHtml: string = html.replace(regex, (_: string, innerContent: string): string => {
        return `<code>${SharedTextUtility.escapeHtmlTags(innerContent)}</code>`;
      });

      return this.turndownService.turndown(escapedHtml);
    }
    return this.turndownService.turndown(html);
  }

  /**
   * Finds the highest heading level present in a markdown string.
   * @returns A number between 1 and 6, 1 representing the highest heading which is a h1.
   */
  public static findHighestHeadingLevel(markdownContent: string): number {
    const headingRegex: RegExp = new RegExp(/^(#{1,6})\s+/gm);
    let highestLevel: number = 7; // Higher than the maximum heading level (6)

    let match: RegExpExecArray | null;
    while ((match = headingRegex.exec(markdownContent)) !== null) {
      const level: number = match[1].length; // The number of `#` characters indicates the heading level
      if (level < highestLevel) {
        highestLevel = level;
      }
    }

    // If no heading is found, return null
    return highestLevel === 7 ? null : highestLevel;
  }
}
